import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ClipboardPaste,
  FileSpreadsheet,
  FileText,
  ImageIcon,
  LoaderCircle,
  Trash2,
  Upload,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { LabLinker, defaultChoices } from "@/components/LabLinker";

const COLS = {
  practicals: [
    "name",
    "subject",
    "year_groups",
    "topic",
    "equipment",
    "review_status",
  ],
  inventory: ["name", "category", "quantity", "units", "location", "date_added"],
  timetable: [
    "teacher_email",
    "teacher_name",
    "week",
    "day",
    "period",
    "class_name",
    "subject",
    "lab",
  ],
};

const show = (v) => (Array.isArray(v) ? v.map((x) => (typeof x === "object" ? `${x.item} ×${x.qty_per_group ?? x.qty_fixed ?? ""}` : x)).join("; ") : String(v ?? ""));
const MAX_IMPORT_FILE_BYTES = 10 * 1024 * 1024;
const recordNeedsRequiredFields = (kind, record, timetableTargetId) => {
  if (!record._incomplete) {
    return false;
  }
  if (record._missing_fields?.length) {
    return record._missing_fields.some((field) => !record[field]);
  }
  if (kind === "inventory") {
    return !record.name || record.quantity === "" || record.quantity === null;
  }
  if (kind === "timetable") {
    const needsTeacher = !timetableTargetId && !record.teacher_name && !record.teacher_email;
    return needsTeacher || !record.week || !record.day || !record.period || !record.class_name;
  }
  return !record.name;
};

export default function ImportDialog({
  kind,
  open,
  onOpenChange,
  onDone,
  timetableTargetId = "",
  timetableTargetName = "",
}) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [workingMessage, setWorkingMessage] = useState("");
  const [labCheck, setLabCheck] = useState(null);
  const [choices, setChoices] = useState({});
  const [saveResult, setSaveResult] = useState(null);

  const checkLabs = useCallback(async (records) => {
    setLabCheck(null);
    try {
      const { data } = await api.post("/import/lab-check", { names: [...new Set(records.map((r) => String(r.lab || "").trim()).filter(Boolean))] });
      setLabCheck(data);
      setChoices(defaultChoices(data));
    } catch (e) {
      setLabCheck({ unmatched: [], matched: [], labs: [] });
      toast.error(errMsg(e));
    }
  }, []);

  const upload = useCallback(async (
    f,
    sheet = "",
    pastedScreenshot = false,
    appendPastedScreenshot = false,
    sourceFileId = "",
  ) => {
    if (f.size > MAX_IMPORT_FILE_BYTES) {
      toast.error("Import files must be 10 MB or smaller");
      return;
    }
    setFile(f);
    setWorkingMessage(
      pastedScreenshot ? "Jonny is reading your screenshot…" : "Jonny is reading your file…",
    );
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", f, f.name || "pasted-screenshot.png");
      fd.append("sheet", sheet);
      fd.append("image_source", pastedScreenshot ? "screenshot" : "file");
      fd.append("source_file_id", sourceFileId);
      const { data } = await api.post("/import/preview", fd);
      const extractedRecords = data.records.map((record) => {
        if (kind !== "practicals" || record.review_status) {
          return record;
        }
        return {
          ...record,
          review_status: ["practical_document", "screenshot"].includes(data.source)
            ? "AI Draft"
            : "Draft",
        };
      });
      const nextPreview = {
        ...data,
        records: extractedRecords,
        sheet: sheet || data.sheets?.[0],
      };
      if (pastedScreenshot && appendPastedScreenshot) {
        setPreview((current) => {
          if (!current || current.source !== "screenshot") return nextPreview;
          return {
            ...current,
            records: [...current.records, ...nextPreview.records],
            screenshot_count: (current.screenshot_count || 1) + 1,
          };
        });
      } else {
        setPreview({
          ...nextPreview,
          screenshot_count: pastedScreenshot ? 1 : undefined,
        });
      }
      if (kind === "timetable") checkLabs(data.records);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      setWorkingMessage("");
    }
  }, [checkLabs, kind]);
  const discardStagedSource = useCallback((currentPreview) => {
    if (!currentPreview?.source_file_id) {
      return;
    }
    api.delete(`/import/source/${kind}/${currentPreview.source_file_id}`).catch(() => {});
  }, [kind]);
  const resetPreview = useCallback(() => {
    discardStagedSource(preview);
    setPreview(null);
    setFile(null);
    setSaveResult(null);
  }, [discardStagedSource, preview]);
  const onPaste = useCallback((e) => {
    if (busy) return;
    const item = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith("image/"));
    const pastedFile = item?.getAsFile()
      || [...(e.clipboardData?.files || [])].find((fileItem) => fileItem.type.startsWith("image/"));
    if (pastedFile) {
      e.preventDefault();
      const appendPastedScreenshot = kind === "inventory" && preview?.source === "screenshot";
      upload(pastedFile, "", kind === "inventory", appendPastedScreenshot);
    }
  }, [busy, kind, preview?.source, upload]);
  const pasteClipboardImage = useCallback(async () => {
    if (busy) return;
    if (!navigator.clipboard?.read) {
      toast.info("Paste a screenshot using your device's paste control or Ctrl/Cmd + V.");
      return;
    }
    try {
      const clipboardItems = await navigator.clipboard.read();
      const imageItem = clipboardItems.find((clipboardItem) => (
        clipboardItem.types.some((type) => type.startsWith("image/"))
      ));
      const imageType = imageItem?.types.find((type) => type.startsWith("image/"));
      if (!imageItem || !imageType) {
        toast.info("Copy a screenshot first, then choose Paste screenshot.");
        return;
      }
      const imageBlob = await imageItem.getType(imageType);
      const imageFile = new File([imageBlob], "pasted-screenshot.png", { type: imageType });
      const append = kind === "inventory" && preview?.source === "screenshot";
      upload(imageFile, "", kind === "inventory", append);
    } catch {
      toast.info("Paste a screenshot using your device's paste control or Ctrl/Cmd + V.");
    }
  }, [busy, kind, preview?.source, upload]);
  useEffect(() => {
    if (!open) return undefined;
    const handlePaste = (event) => {
      if (!event.defaultPrevented) onPaste(event);
    };
    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [onPaste, open]);
  const commit = async () => {
    setWorkingMessage("Saving your imported records…");
    setBusy(true);
    try {
      const used = new Set(preview.records.map((r) => String(r.lab || "").trim()));
      const labMap = kind === "timetable" ? Object.fromEntries(Object.entries(choices).filter(([n]) => used.has(n))) : {};
      const { data } = await api.post("/import/commit", {
        kind,
        records: preview.records,
        lab_map: labMap,
        source: preview.source || "",
        source_file_id: preview.source_file_id || "",
        timetable_target_id: kind === "timetable" ? timetableTargetId : "",
      });
      setPreview(null);
      setFile(null);
      setSaveResult(data);
      onDone?.(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      setWorkingMessage("");
    }
  };
  const remove = (i) => setPreview((p) => ({ ...p, records: p.records.filter((_, j) => j !== i) }));
  const updateRecord = (index, key, value) => {
    setPreview((current) => ({
      ...current,
      records: current.records.map((record, recordIndex) => {
        if (recordIndex !== index) {
          return record;
        }
        const updated = { ...record, [key]: value };
        return {
          ...updated,
          _incomplete: recordNeedsRequiredFields(kind, updated, timetableTargetId),
        };
      }),
    }));
  };
  const inventoryImport = kind === "inventory";
  const practicalImport = kind === "practicals";
  const documentImport = inventoryImport || practicalImport;
  const reviewOptions = ["Draft", "AI Draft", "Approved"];
  const numericColumns = new Set([
    "quantity",
    "day",
    "period",
    "year_group",
    "class_size",
  ]);
  const incompleteCount = preview?.records.filter((record) => (
    recordNeedsRequiredFields(kind, record, timetableTargetId)
  )).length || 0;
  const readyCount = (preview?.records.length || 0) - incompleteCount;
  const sourceLabel = preview?.source === "inventory_document"
    ? "delivery note or invoice (read by AI — please check carefully)"
    : preview?.source === "practical_document"
      ? "practical PDF (read by AI — please check carefully)"
    : preview?.source === "screenshot" && inventoryImport && preview.screenshot_count > 1
      ? `${preview.screenshot_count} screenshots (read by AI — please check carefully)`
      : preview?.source === "screenshot"
        ? "screenshot (read by AI — please check carefully)"
        : file?.name;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          resetPreview();
        }
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent
        className="max-h-[90vh] max-w-4xl overflow-y-auto"
        data-testid="import-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            Import {kind}
            {kind === "timetable" && timetableTargetName
              ? ` for ${timetableTargetName}`
              : ""}
          </DialogTitle>
          <DialogDescription>
            {inventoryImport
              ? "Upload an Excel or CSV file, a pdf or image of a delivery note or invoice, or paste a screenshot of a stocklist. AI-extracted items must be reviewed before they are saved. Try to upload to Ask Jonny if it is not recognised."
              : practicalImport
                ? "Upload an Excel or CSV file, a PDF practical sheet, or a screenshot or photo. Labelled images work better. AI-extracted practicals MUST always be reviewed before they are saved, they cannot be used for booking until review is completed. Try to upload to Ask Jonny if it is not recognised."
              : "Upload an Excel or CSV file, or a screenshot of your spreadsheet. You can also paste a screenshot here."}
          </DialogDescription>
        </DialogHeader>
        {busy && (
          <div
            className="flex items-center gap-3 border border-primary/30 bg-primary/5 px-4 py-3"
            role="status"
            aria-live="polite"
            data-testid="import-working-status"
          >
            <LoaderCircle className="h-5 w-5 animate-spin text-primary" />
            <div>
              <p className="text-sm font-medium">
                {workingMessage || "Jonny is working…"}
              </p>
              <p className="text-xs text-muted-foreground">
                Your import will be ready to review shortly.
              </p>
            </div>
          </div>
        )}
        {saveResult ? (
          <div
            className="space-y-4 border border-primary/30 bg-primary/5 p-5"
            data-testid="import-save-result"
          >
            <div>
              <p className="text-base font-semibold" data-testid="import-save-result-heading">
                Saved to {practicalImport ? "the Practical library" : "Inventory"}
              </p>
              <p className="mt-1 text-sm text-muted-foreground" data-testid="import-save-result-summary">
                {saveResult.created} new, {saveResult.updated} updated
                {saveResult.skipped ? `, ${saveResult.skipped} skipped` : ""}.
              </p>
            </div>
            {saveResult.errors?.length > 0 && (
              <ul className="space-y-1 text-sm text-destructive" data-testid="import-save-result-errors">
                {saveResult.errors.map((error) => <li key={error}>{error}</li>)}
              </ul>
            )}
            {saveResult.warnings?.length > 0 && (
              <ul className="space-y-1 text-sm text-amber-700" data-testid="import-save-result-warnings">
                {saveResult.warnings.map((warning) => <li key={warning}>{warning}</li>)}
              </ul>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setSaveResult(null)}
                data-testid="import-start-another-button"
              >
                Import another
              </Button>
              <Button
                onClick={() => onOpenChange(false)}
                data-testid="import-save-result-done-button"
              >
                Done
              </Button>
            </div>
          </div>
        ) : !preview ? (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="w-full rounded-xl border-2 border-dashed p-10 text-center transition-colors hover:border-primary/60"
              data-testid="import-dropzone"
            >
              <Upload className="mx-auto h-6 w-6 text-muted-foreground" />
              <div className="mt-3 text-sm font-medium">
                {busy
                  ? workingMessage || "Jonny is reading your file…"
                  : documentImport
                    ? "Choose a file, screenshot or PDF"
                    : "Choose a file or screenshot"}
              </div>
              <div className="mt-1 flex flex-wrap justify-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <FileSpreadsheet className="h-3.5 w-3.5" />
                  .xlsx .xls .csv
                </span>
                <span className="flex items-center gap-1">
                  <ImageIcon className="h-3.5 w-3.5" />
                  .png .jpg .webp
                </span>
                {documentImport && (
                  <span className="flex items-center gap-1">
                    <FileText className="h-3.5 w-3.5" />
                    .pdf
                  </span>
                )}
              </div>
            </button>
            {inventoryImport && (
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                className="h-auto min-h-24 flex-col gap-2 px-5"
                onClick={pasteClipboardImage}
                data-testid="inventory-import-paste-screenshot-button"
              >
                <ClipboardPaste className="h-5 w-5" />
                <span>Paste screenshot</span>
                <span className="text-xs text-muted-foreground">Ctrl/Cmd + V</span>
              </Button>
            )}
            </div>
          </div>
        ) : (
          <div className="space-y-3 min-w-0">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="font-medium" data-testid="import-preview-count">
                {preview.records.length} {preview.records.length === 1 ? "row" : "rows"} found
              </span>
              <span className="text-muted-foreground">from {sourceLabel}</span>
              {preview.sheets?.length > 1 && (
                <Select
                  value={preview.sheet}
                  onValueChange={(sheet) =>
                    upload(file, sheet, false, false, preview.source_file_id || "")
                  }
                >
                  <SelectTrigger className="w-48 h-8" data-testid="import-sheet-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{preview.sheets.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            {inventoryImport && preview.source === "screenshot" && (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={pasteClipboardImage}
                  data-testid="inventory-import-paste-another-screenshot-button"
                >
                  <ClipboardPaste className="mr-2 h-4 w-4" />
                  Paste another screenshot
                </Button>
                <span
                  className="text-xs text-muted-foreground"
                  data-testid="inventory-import-paste-another-screenshot-help"
                >
                  Click the button, then use Ctrl/Cmd + V. New rows are added to this review.
                </span>
              </div>
            )}
            {kind === "timetable" && <LabLinker check={labCheck} records={preview.records} choices={choices} setChoices={setChoices} />}
            {Object.keys(preview.mapping || {}).length > 0 && (
              <p className="text-xs text-muted-foreground">Columns matched: {Object.entries(preview.mapping).map(([h, f]) => `${h} → ${f}`).join(", ")}</p>
            )}
            {inventoryImport && preview.source === "inventory_document" && (
              <p className="text-sm font-medium" data-testid="inventory-document-review-heading">
                Review extracted items before adding them to inventory
              </p>
            )}
            {practicalImport && preview.source === "practical_document" && (
              <p className="text-sm font-medium" data-testid="practical-document-review-heading">
                Review extracted practicals before adding them to the library
              </p>
            )}
            {documentImport && preview.source_file_id && (
              <p
                className="text-xs text-muted-foreground"
                data-testid={`${kind}-import-source-attachment-note`}
              >
                The original upload will be attached to each saved {practicalImport ? "practical" : "item"}.
              </p>
            )}
            {incompleteCount > 0 && (
              <p
                className="border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                data-testid="import-incomplete-rows-notice"
              >
                {incompleteCount} incomplete {incompleteCount === 1 ? "row is" : "rows are"} flagged
                below. Complete the required fields or remove the row; it will not be saved yet.
              </p>
            )}
            <div className="max-h-80 overflow-auto rounded-lg border">
              <table className="w-full text-xs" data-testid="import-preview-table">
                <thead className="bg-muted sticky top-0"><tr>{COLS[kind].map((c) => <th key={c} className="text-left p-2 font-semibold">{c}</th>)}<th /></tr></thead>
                <tbody>
                  {preview.records.map((record, index) => (
                    <tr
                      key={index}
                      className={`border-t ${
                        recordNeedsRequiredFields(kind, record, timetableTargetId)
                          ? "bg-amber-50/70"
                          : ""
                      }`}
                      data-testid={
                        recordNeedsRequiredFields(kind, record, timetableTargetId)
                          ? `import-incomplete-row-${index}`
                          : `import-preview-row-${index}`
                      }
                    >
                      {COLS[kind].map((column) => (
                        <td key={column} className="max-w-[240px] p-2">
                          {inventoryImport || practicalImport || kind === "timetable" ? (
                            column === "review_status" ? (
                              <Select
                                value={record[column] || "AI Draft"}
                                onValueChange={(value) => updateRecord(index, column, value)}
                              >
                                <SelectTrigger
                                  className="h-7 min-w-28 text-xs"
                                  data-testid={`import-practicals-review-status-${index}`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {reviewOptions.map((status) => (
                                    <SelectItem
                                      key={status}
                                      value={status}
                                      data-testid={`import-review-option-${status}-${index}`}
                                    >
                                      {status}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                            <Input
                              className="h-7 min-w-24 text-xs"
                              type={
                                numericColumns.has(column)
                                  ? "number"
                                  : column === "date_added"
                                    ? "date"
                                    : "text"
                              }
                              value={Array.isArray(record[column]) ? show(record[column]) : record[column] ?? ""}
                              onChange={(event) =>
                                updateRecord(index, column, event.target.value)
                              }
                              data-testid={`import-${kind}-${column}-${index}`}
                            />
                            )
                          ) : (
                            <span className="block truncate">{show(record[column])}</span>
                          )}
                        </td>
                      ))}
                      <td className="p-2">
                        <button
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => remove(index)}
                          data-testid={`import-row-remove-${index}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                        {recordNeedsRequiredFields(kind, record, timetableTargetId) && (
                          <span
                            className="ml-2 text-[10px] font-medium text-amber-800"
                            data-testid={`import-incomplete-flag-${index}`}
                          >
                            Incomplete
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          hidden
          accept={
            documentImport
              ? ".xlsx,.xls,.csv,.pdf,.png,.jpg,.jpeg,.webp"
              : ".xlsx,.xls,.csv,.png,.jpg,.jpeg,.webp"
          }
          onChange={(event) => {
            const selected = event.target.files[0];
            event.target.value = "";
            if (selected) {
              const isInventoryScreenshot = (
                kind === "inventory" && selected.type.startsWith("image/")
              );
              upload(selected, "", isInventoryScreenshot);
            }
          }}
          data-testid="import-file-input"
        />
        {preview && (
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={resetPreview}
              data-testid="import-reset-button"
            >
              Start again
            </Button>
            <Button
              onClick={commit}
              disabled={
                busy
                || readyCount === 0
                || (kind === "timetable" && !labCheck)
              }
              data-testid="import-commit-button"
            >
              {busy
                ? "Saving…"
                : `Save ${readyCount} ${readyCount === 1 ? "row" : "rows"}`}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
